
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { WorkoutType, WORKOUT_COLORS } from "@/models/WorkoutType";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "react-i18next";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";

interface WorkoutTypeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: WorkoutType;
  onSubmit: (data: z.infer<typeof formSchema>) => Promise<void>;
}

const formSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, "Workout type name must be at least 2 characters."),
  description: z.string().optional(),
  color: z.string(),
  is_active: z.boolean().default(true),
});

const WorkoutTypeForm: React.FC<WorkoutTypeFormProps> = ({
  open,
  onOpenChange,
  initialData,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      color: WORKOUT_COLORS[0],
      is_active: true,
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        id: initialData.id,
        name: initialData.name,
        description: initialData.description || "",
        color: initialData.color,
        is_active: initialData.is_active,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        color: WORKOUT_COLORS[0],
        is_active: true,
      });
    }
  }, [initialData, form]);

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    await onSubmit(data);
  };

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 sm:space-y-6 h-full flex flex-col">
        <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('workoutTypes.name')}</FormLabel>
                <FormControl>
                  <Input 
                    placeholder={t('workoutTypes.name')} 
                    {...field} 
                    className="text-base h-9 sm:h-10"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('workoutTypes.description')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('workoutTypes.description')}
                    {...field}
                    value={field.value || ""}
                    className="text-base min-h-[60px] sm:min-h-[80px]"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('workoutTypes.color')}</FormLabel>
                <FormControl>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {WORKOUT_COLORS.map((color) => (
                      <div
                        key={color}
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full cursor-pointer transition-all hover:scale-110 ${
                          field.value === color
                            ? "border-2 border-black dark:border-white shadow-lg"
                            : "border-2 border-transparent hover:border-gray-300"
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => form.setValue("color", color)}
                      />
                    ))}
                  </div>
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('workoutTypes.status')}</FormLabel>
                <Select onValueChange={(value) => field.onChange(value === "true")} value={field.value.toString()}>
                  <FormControl>
                    <SelectTrigger className="text-base h-9 sm:h-10">
                      <SelectValue placeholder={t('workoutTypes.selectStatus')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="true">{t('workoutTypes.active')}</SelectItem>
                    <SelectItem value="false">{t('workoutTypes.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </div>
        <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row gap-2 sm:gap-0 sm:space-x-2 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto text-sm h-9 sm:h-10"
          >
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit"
            className="w-full sm:w-auto text-sm h-9 sm:h-10"
          >
            {initialData ? t('common.save') : t('common.add')}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (isMobile || isTablet) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-screen w-full rounded-none border-0 p-0 flex flex-col">
          <SheetHeader className="p-4 sm:p-6 border-b flex-shrink-0">
            <SheetTitle className="text-lg sm:text-xl">
              {initialData ? t('workoutTypes.edit') : t('workoutTypes.add')}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 p-4 sm:p-6 overflow-hidden">
            {formContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="pb-3">
          <DialogTitle className="text-lg sm:text-xl">
            {initialData ? t('workoutTypes.edit') : t('workoutTypes.add')}
          </DialogTitle>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default WorkoutTypeForm;
